import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import * as io from 'socket.io-client';
import { AuthenticationService } from '../authentication/authentication.service';
import { EnvVarsService } from 'src/app/env-vars.service';
import { max } from 'lodash';

export type TSocket = SocketIOClient.Socket;

@Injectable({
  providedIn: 'root',
})
export class SocketsService {
  private WSLocalStorage = {
    key: 'SOCKET_INFO',
    maxLogs: 100,
  };
  private socket!: TSocket;

  public globalSocket = new ReplaySubject<TSocket>(1); // ReplaySubject avoids null issues

  private SOCKET_URL: string;

  private retryCount = 0;
  private maxRetryLog = 3;
  private connectionLogs: { event: string; timestamp: string }[] = [];

  constructor(
    private _auth: AuthenticationService,
    private environment: EnvVarsService
  ) {
    this.SOCKET_URL = this.environment.getEnvKey('SOCKET_ENDPOINT');
    this.initSocket();
    this.setupEventListeners();
  }

  private initSocket(): void {
    this.socket = io.default(this.SOCKET_URL, {
      query: {
        token: this._auth.getToken(),
      },
      reconnectionDelay: 10000,
    });

    this.globalSocket.next(this.socket);
  }
  private handleConnectionError(event: string, error: any): void {
    if (this.retryCount < this.maxRetryLog) {
      console.error(
        `SOCKET ${event.toUpperCase()} - Attempt ${this.retryCount + 1}`,
        error
      );
    } else if (this.retryCount === this.maxRetryLog) {
      console.error(
        `SOCKET ${event.toUpperCase()} - Too many retries, suppressing further logs`
      );
    }
    this.retryCount++;
  }
  private setupEventListeners(): void {
    this.socket.on('connect', () => {
      this.logConnectionEvent('Connected');
      this.retryCount = 0; // Reset retry count on successful connection
    });

    this.socket.on('disconnect', (reason) => {
      this.logConnectionEvent(`Disconnected: ${reason}`);
    });

    this.socket.on('connect_error', (err) =>
      this.handleConnectionError('connect_error', err)
    );
    this.socket.on('connect_failed', (err) =>
      this.handleConnectionError('connect_failed', err)
    );
  }
  public logConnectionEvent(event: string): void {
    const prevData = JSON.parse(
      localStorage.getItem(this.WSLocalStorage.key) || '[]'
    );
    const timestamp = new Date().toISOString();

    // Add new event and ensure only the last 100 are kept
    const updatedData = [...prevData, { event, timestamp }].slice(
      -this.WSLocalStorage.maxLogs
    );

    localStorage.setItem(this.WSLocalStorage.key, JSON.stringify(updatedData));
  }

  public getSocket(): TSocket {
    return this.socket;
  }

  // Called at app startup to guarantee the socket is instantiated and connected.
  // SocketsService is providedIn: 'root' but Angular only creates it on first
  // injection — calling this makes the eager-init intent explicit at the call site.
  public ensureConnected(): void {}
}
